import { type ApiStorageTransaction } from '@Model';
import { useMemo, useRef, useState } from 'react';
import { FrameEditStorageTransaction } from './FrameEditStorageTransaction';
import { BookingFlowContext } from './StorageTransaction/bookingFlowContext';
import { usePrepareBookingDraft } from './usePrepareBookingDraft';

interface IProps {
    initialDraftId: string;
    focusMaterial: boolean;
    onBooked?: () => void;
    onClose: () => void;
}

/** The booking mask in its overlay, which after a booking closes or moves on to the next draft. */
// @ai-generated 2026-10-23T17:40:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function BookingFlow({ initialDraftId, focusMaterial, onBooked, onClose }: IProps) {
    const prepareDraft = usePrepareBookingDraft();
    const [draftId, setDraftId] = useState(initialDraftId);
    const nextRequested = useRef(false);

    const flow = useMemo(
        () => ({
            requestNext: (next: boolean) => {
                nextRequested.current = next;
            },
            focusMaterial: focusMaterial || draftId !== initialDraftId,
        }),
        [focusMaterial, draftId, initialDraftId],
    );

    const onSave = async (booked?: ApiStorageTransaction) => {
        onBooked?.();

        const next =
            nextRequested.current && booked
                ? await prepareDraft({
                      storageLocationId: booked.storage_location.foreign_key,
                      type: booked.type,
                      occurredAt: booked.occurred_at,
                  })
                : undefined;
        if (next === undefined) {
            onClose();
        } else {
            setDraftId(next.id);
        }
        return true;
    };

    return (
        <BookingFlowContext.Provider value={flow}>
            <FrameEditStorageTransaction
                key={draftId}
                primaryKey={draftId}
                embedded
                canEdit
                canCreate
                canDelete={false}
                events={{ onBack: undefined, onAdd: undefined, onClose, onSave }}
            />
        </BookingFlowContext.Provider>
    );
}
